import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import ArticleCard from "@/components/ArticleCard";
import SchemaMarkup from "@/components/SchemaMarkup";
import { articles } from "@/mocks/articles";
import { testimonials } from "@/mocks/testimonials";
import { formatViews, formatDate } from "@/lib/utils";
import { useState } from "react";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [testimonialIdx, setTestimonialIdx] = useState(0);

  const article = articles.find((a) => a.slug === id);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-32 px-4 text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-900 mb-4">
            文章未找到
          </h1>
          <p className="text-foreground-500 mb-6">抱歉，该文章可能已被删除或不存在。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-primary-500 text-white px-6 py-3 text-sm font-medium rounded hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 4);

  return (
    <>
      <SchemaMarkup
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.image,
          datePublished: article.date,
          dateModified: article.date,
          author: {
            "@type": "Organization",
            name: article.author,
          },
          publisher: {
            "@type": "Organization",
            name: "91EatGossip",
            logo: { "@type": "ImageObject", url: "http://msldyun.cn/logo.png" },
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `http://msldyun.cn/article/${article.slug}`,
          },
        }}
      />
      <SchemaMarkup
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "http://msldyun.cn/" },
            { "@type": "ListItem", position: 2, name: article.category, item: `http://msldyun.cn/category/${article.categorySlug}` },
            { "@type": "ListItem", position: 3, name: article.title },
          ],
        }}
      />

      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-20">
          <article>
            <section className="relative min-h-[400px] md:min-h-[500px] flex items-center">
              <div className="w-full grid grid-cols-1 lg:grid-cols-2 min-h-[400px] md:min-h-[500px]">
                <div className="flex flex-col justify-center px-6 md:px-12 lg:px-16 py-16 lg:py-0 order-2 lg:order-1 bg-background-50">
                  <div className="max-w-xl">
                    <Breadcrumb
                      items={[
                        { label: "首页", path: "/" },
                        { label: article.category, path: `/category/${article.categorySlug}` },
                        { label: article.title },
                      ]}
                    />
                    <div className="flex items-center gap-3 mt-6 mb-4">
                      <span className="px-2.5 py-1 bg-primary-50 text-primary-600 text-xs font-medium rounded">
                        {article.category}
                      </span>
                      {article.isHot && (
                        <span className="px-2.5 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded">
                          热门
                        </span>
                      )}
                    </div>
                    <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                      {article.title}
                    </h1>
                    <p className="text-base text-foreground-600 leading-relaxed mb-6 line-clamp-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-foreground-500">
                      <span>{article.author}</span>
                      <span>{formatDate(article.date)}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line" />
                        {formatViews(article.views)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="relative order-1 lg:order-2 min-h-[250px] lg:min-h-[500px]">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-background-50/50 via-transparent to-transparent lg:block hidden" />
                </div>
              </div>
            </section>

            <section className="bg-background-50 py-12 md:py-20 px-4 md:px-8 lg:px-12">
              <div className="w-full max-w-4xl mx-auto">
                <div className="prose prose-lg max-w-none">
                  {article.content.split("\n\n").map((paragraph, index) => (
                    <p
                      key={index}
                      className="text-base md:text-lg text-foreground-700 leading-relaxed mb-6"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>

                {article.tags && article.tags.length > 0 && (
                  <div className="mt-10 pt-6 border-t border-background-200/70">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-foreground-500">标签：</span>
                      {article.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          </article>

          <section className="bg-background-100 py-16 md:py-24 px-4 md:px-8 lg:px-12">
            <div className="w-full max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-10">
                （读者热评）
              </h2>
              <div className="text-left">
                <div className="flex items-center gap-2 mb-6">
                  <div className="px-3 py-1.5 bg-foreground-900 text-background-50 rounded-full text-xs font-medium flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-500" />
                    <span>{testimonials[testimonialIdx].rating}</span>
                  </div>
                </div>
                <blockquote className="font-heading text-lg text-foreground-800 leading-relaxed mb-8">
                  &ldquo;{testimonials[testimonialIdx].quote}&rdquo;
                </blockquote>
                <div className="flex items-center gap-3">
                  <img
                    src={testimonials[testimonialIdx].avatar}
                    alt={testimonials[testimonialIdx].author}
                    className="w-12 h-12 rounded-full object-cover"
                    loading="lazy"
                  />
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">
                      {testimonials[testimonialIdx].author}
                    </p>
                    <p className="text-xs text-foreground-500">
                      {testimonials[testimonialIdx].role}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 mt-8">
                <button
                  onClick={() =>
                    setTestimonialIdx((i) => (i === 0 ? testimonials.length - 1 : i - 1))
                  }
                  className="w-10 h-10 border border-foreground-300 flex items-center justify-center text-foreground-600 hover:bg-background-200 transition-colors"
                  aria-label="上一条评价"
                >
                  <i className="ri-arrow-left-line" />
                </button>
                <button
                  onClick={() =>
                    setTestimonialIdx((i) => (i === testimonials.length - 1 ? 0 : i + 1))
                  }
                  className="w-10 h-10 bg-foreground-900 flex items-center justify-center text-background-50 hover:bg-foreground-800 transition-colors"
                  aria-label="下一条评价"
                >
                  <i className="ri-arrow-right-line" />
                </button>
              </div>
            </div>
          </section>

          {relatedArticles.length > 0 && (
            <section className="bg-foreground-950 py-16 md:py-24 px-4 md:px-8 lg:px-12">
              <div className="w-full max-w-7xl mx-auto">
                <div className="flex items-center gap-3 mb-10">
                  <span className="px-3 py-1.5 border border-background-400 text-background-300 text-xs uppercase tracking-wide rounded-full">
                    相关推荐
                  </span>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50">
                    同类热瓜
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                  {relatedArticles.map((a) => (
                    <ArticleCard key={a.id} article={a} variant="compact" />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}